/* GENERATED FILE — DO NOT EDIT.
 *
 * Emitted from `contracts/openapi/ai-rulegen.yaml` by `tools/openapi_ts/generate.py`.
 * Regenerate with `python -m tools.openapi_ts.generate`;
 * `test_generated_client_matches_the_contract` fails the build if this file drifts from the spec.
 *
 * Hard rule 10 makes the spec authoritative, so editing this file by hand would put the client and
 * the server behind two different sources of truth — which is the drift the rule exists to prevent.
 *
 * Each type has a matching `is<Name>` guard. The types are for `tsc`; the guards are because a
 * response is data from another process, and a compile-time type is only a claim about it.
 */


/**
 * Which stage produced a suggestion, ordered by how much is *known* rather than inferred.
 * `schema_declared` — the source itself says so. A `NOT NULL` column yields a rule that cannot be
 * wrong about the constraint. `schema_inferred` — derived from naming or type conventions, still
 * deterministic and weaker, because a convention holds until a customer names things differently.
 * `model_proposed` — a language model's suggestion, never labelled as either of the others,
 * because the label is what tells a reviewer which to read hardest.
 */
export type CandidateSource = 'schema_declared' | 'schema_inferred' | 'model_proposed';

export const CANDIDATE_SOURCES: readonly CandidateSource[] = ['schema_declared', 'schema_inferred', 'model_proposed'];

export function isCandidateSource(value: unknown): value is CandidateSource {
  return (['schema_declared', 'schema_inferred', 'model_proposed'] as readonly unknown[]).includes(value);
}

/**
 * Why a candidate was refused. Closed, because these are counted: a generator whose output is
 * mostly refused is one this deployment should not run, and that is only answerable if the reasons
 * are a vocabulary. `unknown_column` and `not_visible` are deliberately separate. The first is a
 * model inventing something; the second is a model naming something real that *this person* may
 * not see. One is a model-capability problem and the other is an access-control event.
 */
export type RejectionReason = 'unknown_column' | 'not_visible' | 'malformed' | 'duplicate' | 'unsupported_kind';

export const REJECTION_REASONS: readonly RejectionReason[] = ['unknown_column', 'not_visible', 'malformed', 'duplicate', 'unsupported_kind'];

export function isRejectionReason(value: unknown): value is RejectionReason {
  return (['unknown_column', 'not_visible', 'malformed', 'duplicate', 'unsupported_kind'] as readonly unknown[]).includes(value);
}

/**
 * What happened to stage 2 on one generation run. **Six states, and none of them is a
 * fabrication.** The requirement this exists for is that a client may never show "AI suggested"
 * when no AI ran, and may never report a stage-1-only run as though a model had examined the
 * schema and found nothing worth saying. A boolean cannot hold those apart, which is why
 * `model_consulted` is *derived* from this rather than tracked beside it. `not_configured` — no
 * proposer is wired in this deployment; the supported default for an air-gapped install.
 * `no_route` — a proposer is wired and this tenant has no model routed, or switched theirs off; a
 * configuration answer, not an outage. `unavailable` — a model was routed and could not be
 * reached; **our** outage. `rejected` — the model answered and the validator refused it, or the
 * capability matrix refused the model; a capability signal. `contributed` — the model answered and
 * the validator accepted it, and it is the **only** state under which any suggestion carries
 * `source: model_proposed`. `nothing_to_ask` — stage 1 covered every column, or the model added
 * nothing beyond it.
 */
export type ModelStatus = 'not_configured' | 'no_route' | 'unavailable' | 'rejected' | 'contributed' | 'nothing_to_ask';

export const MODEL_STATUSES: readonly ModelStatus[] = ['not_configured', 'no_route', 'unavailable', 'rejected', 'contributed', 'nothing_to_ask'];

export function isModelStatus(value: unknown): value is ModelStatus {
  return (['not_configured', 'no_route', 'unavailable', 'rejected', 'contributed', 'nothing_to_ask'] as readonly unknown[]).includes(value);
}

export interface Suggestion {
  /**
   * The suggestion's own id, which a rule draft cites. **Regenerated on every run**, because a
   * regeneration is a delete-and-insert — so an id names a recommendation as it stood when
   * somebody looked at it, not a durable entity a rule can point at.
   */
  readonly id: string;
  readonly kind: 'not_null' | 'unique' | 'accepted_values' | 'range' | 'freshness' | 'pattern';
  readonly columns: readonly string[];
  readonly parameters: Readonly<Record<string, string>>;
  readonly source: CandidateSource;
  /**
   * Why this rule was proposed, in words a steward reads. **Always host-written**, including for a
   * model candidate: carrying a model's own rationale would put unvalidated model text on a
   * screen, which is the leak hard rule 8 exists to prevent.
   */
  readonly rationale: string;
  /**
   * Which validator checks ran. An empty list is refused at construction and by a CHECK constraint
   * — it is what a validator that stopped checking looks like. **Two checks `MODULE_PROMPTS.md`
   * specifies are deliberately absent, and this list says so rather than quietly matching the
   * brief.** *Compiles to valid SQL for this dialect* needs the compiler exposed as a function,
   * which `PostgresRuleExecutor` holds internally. *Dry-run measures the failure rate, reject
   * above 40%* is an **ADR-004 rung 2** operation against a customer's production table, and
   * running one per suggestion would put a scan behind a screen somebody refreshes. A `checks`
   * list that silently matched the specification while omitting two of its items is precisely the
   * shape that makes a validator look stronger than it is.
   */
  readonly checks: readonly string[];
}

export function isSuggestion(value: unknown): value is Suggestion {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    (['not_null', 'unique', 'accepted_values', 'range', 'freshness', 'pattern'] as readonly unknown[]).includes(candidate['kind']) &&
    Array.isArray(candidate['columns']) &&
    (candidate['columns'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    candidate['parameters'] !== undefined &&
    (['schema_declared', 'schema_inferred', 'model_proposed'] as readonly unknown[]).includes(candidate['source']) &&
    typeof candidate['rationale'] === 'string' &&
    Array.isArray(candidate['checks']) &&
    (candidate['checks'] as readonly unknown[]).every((item) => typeof item === 'string')
  );
}

export interface GenerationOutcome {
  readonly dataset_id: string;
  /**
   * So a caller can report "6 suggestions from 340 columns" rather than a bare list whose
   * completeness nobody can judge.
   */
  readonly columns_considered: number;
  /**
   * How many suggestions this run stored. **A count, and it used to be the whole list.** This
   * carried `array of Suggestion` — a second copy of what `GET .../suggestions` serves, and a
   * divergent one: the items were projected without the `id` that `Suggestion` requires and that a
   * rule draft cites, because an id is minted when the row is stored and this projection never
   * read it back. So the response violated its own schema, and a client validating items refused
   * the whole outcome. Two shapes for one concept is the defect; the count is what the field was
   * actually used for. A run's *numbers* belong here and its *rows* belong on the listing: those
   * numbers describe a generation, and the list describes the dataset's current state. Sums to
   * `by_source`, which carries the same total broken down by evidence tier.
   */
  readonly accepted: number;
  /**
   * Accepted suggestions per source. This is the module's central claim made measurable: the share
   * needing no model is a number rather than an assertion, and stays answerable after stage 2
   * lands — which is exactly when somebody would otherwise stop being able to tell.
   */
  readonly by_source: Readonly<Record<string, number>>;
  /**
   * Counts per `RejectionReason`. **Counts, never a rejected candidate's detail** — a detail can
   * name a column, and a rejected *model* candidate is the one place a column name may not be one
   * we sent.
   */
  readonly rejected: Readonly<Record<string, number>>;
  /**
   * Numeric columns stage 1 deliberately proposed nothing for, because it has a type and not a
   * distribution. Reported so "we looked and had nothing to say" is distinguishable from "we did
   * not look" — and because it is the list a profile-backed stage 1 would start from.
   */
  readonly unbounded_numeric_columns?: readonly string[];
  /**
   * Whether a validated model candidate came out of this run. **Derived from `model_status`**
   * rather than tracked beside it, so the coarse answer and the precise one cannot disagree — two
   * fields that could is how a screen comes to show "AI suggested" for output no model produced.
   */
  readonly model_consulted: boolean;
  readonly model_status: ModelStatus;
  /**
   * How many raw candidates stage 2 returned, **before** this module's validator judged them. The
   * gap between this and `by_source.model_proposed` is the measurement `core.ai.evals` gates on: a
   * model whose output is mostly refused is one this deployment should not run the feature on.
   */
  readonly model_candidates: number;
  /**
   * Why stage 2 did not run, when it did not. `null` means it did. Named rather than silent: a
   * deployment with no model produces stage-1-only suggestions that look identical to a run where
   * the model contributed nothing, and those are different facts about whether the deployment is
   * working.
   */
  readonly model_unavailable_reason?: string | null;
}

export function isGenerationOutcome(value: unknown): value is GenerationOutcome {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['dataset_id'] === 'string' &&
    typeof candidate['columns_considered'] === 'number' &&
    typeof candidate['accepted'] === 'number' &&
    candidate['by_source'] !== undefined &&
    candidate['rejected'] !== undefined &&
    typeof candidate['model_consulted'] === 'boolean' &&
    (['not_configured', 'no_route', 'unavailable', 'rejected', 'contributed', 'nothing_to_ask'] as readonly unknown[]).includes(candidate['model_status']) &&
    typeof candidate['model_candidates'] === 'number'
  );
}

export interface SuggestionList {
  readonly dataset_id: string;
  readonly suggestions: readonly Suggestion[];
}

export function isSuggestionList(value: unknown): value is SuggestionList {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['dataset_id'] === 'string' &&
    Array.isArray(candidate['suggestions']) &&
    (candidate['suggestions'] as readonly unknown[]).every((item) => isSuggestion(item))
  );
}

/**
 * **A named schema rather than an inline item, and the generator is why.** An `items:` block
 * written inline emits `Record<string, unknown>` in the TypeScript client, so the envelope guard
 * would check `Array.isArray` and validate nothing about the entries — which is the exact defect
 * S-71 fixed across four screens. A `$ref` gets a real per-item guard, and the badge count is a
 * number a screen renders.
 */
export interface DatasetSuggestionCount {
  readonly dataset_id: string;
  /**
   * At least one, always: a dataset with none is absent from this list rather than present with
   * zero, because absence is what "not generated for" looks like.
   */
  readonly suggestions: number;
}

export function isDatasetSuggestionCount(value: unknown): value is DatasetSuggestionCount {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['dataset_id'] === 'string' &&
    typeof candidate['suggestions'] === 'number'
  );
}

export interface SuggestionCounts {
  readonly counts: readonly DatasetSuggestionCount[];
}

export function isSuggestionCounts(value: unknown): value is SuggestionCounts {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['counts']) &&
    (candidate['counts'] as readonly unknown[]).every((item) => isDatasetSuggestionCount(item))
  );
}

export interface RuleDraft {
  readonly suggestion_id: string;
  /**
   * The dataset the suggestion turned out to be about — **read from the stored row, never taken
   * from the caller.** A caller-supplied pair could prefill one dataset's column against
   * another's, and the authorization is performed against this value.
   */
  readonly dataset_id: string;
  readonly kind: 'not_null' | 'unique' | 'accepted_values' | 'range' | 'freshness' | 'pattern';
  readonly columns: readonly string[];
  readonly parameters: Readonly<Record<string, string>>;
  /**
   * Host-written for every source, including `model_proposed` — a model's own sentence never
   * reaches a stored suggestion, so putting this in a rule's description carries no model text
   * into `rules.rule`.
   */
  readonly rationale: string;
  readonly source: CandidateSource;
  /**
   * A proposed rule name a person edits. Derived rather than left empty, because an empty required
   * field is what makes a prefill feel broken — and deliberately obvious rather than clever, since
   * a name a reviewer keeps by accident is worse than one they can see needs fixing.
   */
  readonly name: string;
  readonly description: string;
  /**
   * Carries `suggested-from:<suggestion_id>`, ready to send straight back on `POST /v1/rules`.
   * That endpoint already accepts tags, so provenance needs no schema change to `core.rules` and
   * no knowledge of suggestions in that module.
   */
  readonly tags: readonly string[];
  /**
   * Always `true`, and on the wire rather than assumed by a client. A prefilled field must read as
   * *proposed* rather than as entered, and a screen deciding that for itself is a screen that can
   * quietly stop doing it.
   */
  readonly requires_review: boolean;
  /**
   * Where the rule is actually created. Served rather than documented, for the reason
   * `accepting_a_suggestion` is: the absence of an accept route here is a governance decision, and
   * a client paraphrasing it would drift from the route list.
   */
  readonly creates_rule_at: string;
  /** What still has to happen afterwards, in the platform's own words. */
  readonly then: string;
}

export function isRuleDraft(value: unknown): value is RuleDraft {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['suggestion_id'] === 'string' &&
    typeof candidate['dataset_id'] === 'string' &&
    (['not_null', 'unique', 'accepted_values', 'range', 'freshness', 'pattern'] as readonly unknown[]).includes(candidate['kind']) &&
    Array.isArray(candidate['columns']) &&
    (candidate['columns'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    candidate['parameters'] !== undefined &&
    typeof candidate['rationale'] === 'string' &&
    (['schema_declared', 'schema_inferred', 'model_proposed'] as readonly unknown[]).includes(candidate['source']) &&
    typeof candidate['name'] === 'string' &&
    typeof candidate['description'] === 'string' &&
    Array.isArray(candidate['tags']) &&
    (candidate['tags'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    typeof candidate['requires_review'] === 'boolean' &&
    typeof candidate['creates_rule_at'] === 'string' &&
    typeof candidate['then'] === 'string'
  );
}

export interface RulegenCapabilities {
  readonly rung: number;
  readonly proposable_kinds: readonly string[];
  /**
   * Kinds this module refuses to propose, each with its reason. `custom_sql` is the one that
   * matters and it is a security boundary: a suggestion carrying arbitrary SQL is one click from
   * running against a customer's production database, and no validator makes that safe — checking
   * it parses says nothing about what it does.
   */
  readonly never_proposed: Readonly<Record<string, string>>;
  readonly sources: readonly CandidateSource[];
  readonly checks: readonly string[];
  readonly rejection_reasons: readonly RejectionReason[];
  /**
   * **The bound is the tenant, not the individual user, and this field states that.** The column
   * list a candidate is judged against is read on a session bound to the caller's tenant under
   * row-level security; the platform has no per-column grant model, so two callers in one tenant
   * receive the same list. `MODULE_PROMPTS.md` asks for "a column the requesting user cannot see",
   * which is finer than the platform can currently express. What is enforced is real — a candidate
   * naming another tenant's column, or one not on this dataset, is refused — and the gap is named
   * here rather than implied, because a published statement about a security control that
   * overstates it is worse than one that omits it.
   */
  readonly authorization_policy: string;
  readonly accepting_a_suggestion: string;
}

export function isRulegenCapabilities(value: unknown): value is RulegenCapabilities {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['rung'] === 'number' &&
    Array.isArray(candidate['proposable_kinds']) &&
    (candidate['proposable_kinds'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    candidate['never_proposed'] !== undefined &&
    Array.isArray(candidate['sources']) &&
    (candidate['sources'] as readonly unknown[]).every((item) => isCandidateSource(item)) &&
    Array.isArray(candidate['checks']) &&
    (candidate['checks'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    Array.isArray(candidate['rejection_reasons']) &&
    (candidate['rejection_reasons'] as readonly unknown[]).every((item) => isRejectionReason(item)) &&
    typeof candidate['authorization_policy'] === 'string' &&
    typeof candidate['accepting_a_suggestion'] === 'string'
  );
}
